import { useLingui } from '@lingui/react/macro';
import { useCallback, useRef, useState, type ReactNode } from 'react';

import { ApiError, type StepUpGrant } from '@/shared/http';
import { StepUpAuthorizeProvider, type StepUpAuthorize, type StepUpAuthorizeRequest } from '@/shared/patterns/step-up-port';

import { grantConfirm, STEP_UP_CANCELLED } from './step-up';
import { StepUpRuntimeContext } from './step-up-runtime';
import { TransactionSheet } from './transaction-sheet';

interface Pending {
  readonly serial: number;
  readonly request: StepUpAuthorizeRequest;
  readonly call: (grant: StepUpGrant) => Promise<unknown>;
  readonly settle: (outcome: { readonly result: unknown } | { readonly error: Error }) => void;
}

const cancelled = () => new ApiError({ kind: 'canceled', payload: { type: 'error', code: STEP_UP_CANCELLED } });

/**
 * A casca informa quem está logado: o `username` do Cognito vai no `POST /Auth/StartAuth` (o
 * `user.id` = `getUsername()` do legado, `identityContext.tsx:111`). Lido na hora do envio. Também monta o painel
 * do step-up novo (`useStepUpAuthorize`), um por vez, como o `StepUpProvider` do legado (`stepUpContext.tsx`).
 */
export function StepUpProvider({ username, children }: { readonly username: () => string | undefined; readonly children: ReactNode }) {
  const { t } = useLingui();
  const [pending, setPending] = useState<Pending | null>(null);
  const [open, setOpen] = useState(false);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const serial = useRef(0);

  const authorize = useCallback<StepUpAuthorize>(<T,>(request: StepUpAuthorizeRequest, call: (grant: StepUpGrant) => Promise<T>) => {
    const show = () =>
      new Promise<T>((resolve, reject) => {
        let settled = false;
        serial.current += 1;
        setPending({
          serial: serial.current,
          request,
          call,
          settle: (outcome) => {
            if (settled) return;
            settled = true;
            setOpen(false);
            if ('result' in outcome) resolve(outcome.result as T);
            else reject(outcome.error);
          },
        });
        setOpen(true);
      });
    // Um painel por vez: um pedido que chega com outro aberto espera o anterior terminar (legado).
    const next = queue.current.then(show, show);
    queue.current = next.catch(() => undefined);
    return next;
  }, []);

  const request = pending?.request;
  return (
    <StepUpRuntimeContext value={{ username }}>
      <StepUpAuthorizeProvider value={authorize}>
        {children}
        {pending && request ? (
          <TransactionSheet<unknown>
            key={pending.serial}
            open={open}
            onOpenChange={(next) => {
              if (!next) pending.settle({ error: cancelled() });
            }}
            slot="step-up-sheet"
            title={request.title ?? t`Confirme a operação`}
            steps={request.description ? ['review', 'code'] : ['code']}
            step="code"
            description={request.description}
            confirm={grantConfirm({ action: request.action, resourceId: request.resourceId }, pending.call)}
            onDone={(result) => pending.settle({ result })}
            onOperationError={(error) => {
              if (request.errors === 'panel') return false;
              pending.settle({ error });
              return true;
            }}
            submitLabel={request.submitLabel ?? t`Confirmar operação`}
            size="sm"
            {...(request.destructive ? { destructive: true } : {})}
          />
        ) : null}
      </StepUpAuthorizeProvider>
    </StepUpRuntimeContext>
  );
}
export { useStepUpRuntime } from './step-up-runtime';
